import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Mail, Trash2, Inbox as InboxIcon } from "lucide-react";
import { toast } from "sonner";
import { SectionHeading } from "@/components/SectionHeading";
import AdminGate from "@/components/admin/AdminGate";
import { getContacts, deleteContact } from "@/lib/api";
import { STORE } from "@/constants/testIds";

const NarrowContainer = ({ children }) => (
  <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">{children}</div>
);

function formatDate(value) {
  try {
    return new Date(value).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return value;
  }
}

function MessageList() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-contacts"],
    queryFn: getContacts,
  });

  const del = useMutation({
    mutationFn: deleteContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-contacts"] });
      toast.success("Message deleted");
    },
    onError: () => toast.error("Could not delete", { description: "Please try again." }),
  });

  if (isLoading) {
    return (
      <div className="py-10 text-center text-sm text-[color:rgba(11,18,32,0.6)]">
        Loading messages...
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-xl border border-[color:rgba(155,44,44,0.3)] bg-[color:rgba(155,44,44,0.06)] px-4 py-3 text-sm text-[#9b2c2c]">
        Could not load messages. Please refresh and try again.
      </div>
    );
  }

  const messages = data || [];

  if (messages.length === 0) {
    return (
      <div
        data-testid={STORE.inboxEmpty}
        className="rounded-2xl border border-dashed border-[color:rgba(10,27,51,0.2)] bg-white px-6 py-14 text-center"
      >
        <span className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--ibp-offwhite)] text-[color:rgba(11,18,32,0.4)]">
          <InboxIcon className="h-6 w-6" />
        </span>
        <p className="text-base font-semibold text-[var(--ibp-ink)]">No messages yet</p>
        <p className="mt-1 text-sm text-[color:rgba(11,18,32,0.6)]">
          New contact-form submissions will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm font-medium text-[color:rgba(11,18,32,0.6)]">
        {messages.length} message{messages.length === 1 ? "" : "s"}
      </p>
      {messages.map((m) => (
        <article
          key={m.id}
          data-testid={STORE.inboxMessageCard}
          className="rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-5 shadow-[0_10px_30px_rgba(7,20,39,0.05)]"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-base font-semibold text-[var(--ibp-ink)]">{m.name}</p>
              <a
                href={`mailto:${m.email}`}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0F3D8A] hover:underline"
              >
                <Mail className="h-3.5 w-3.5" /> {m.email}
              </a>
            </div>
            <div className="flex items-center gap-3">
              <time className="text-xs text-[color:rgba(11,18,32,0.5)]">
                {formatDate(m.created_at)}
              </time>
              <button
                type="button"
                data-testid={STORE.inboxDeleteButton}
                onClick={() => del.mutate(m.id)}
                disabled={del.isPending}
                aria-label="Delete message"
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[color:rgba(11,18,32,0.45)] transition-colors hover:bg-[color:rgba(155,44,44,0.08)] hover:text-[#9b2c2c] disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
          <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-[color:rgba(11,18,32,0.8)]">
            {m.message}
          </p>
        </article>
      ))}
    </div>
  );
}

export default function AdminInbox() {
  return (
    <div data-testid={STORE.inboxPage} className="bg-[var(--ibp-offwhite)]">
      <section className="bg-[var(--ibp-navy-950)] py-12 text-white sm:py-16">
        <NarrowContainer>
          <SectionHeading
            tone="navy"
            kicker="Store admin"
            title="Contact inbox"
            subtitle="Messages submitted through the contact form. Reply by emailing the sender directly."
          />
        </NarrowContainer>
      </section>

      <section className="py-14 sm:py-20">
        <NarrowContainer>
          <AdminGate>
            <MessageList />
          </AdminGate>
        </NarrowContainer>
      </section>
    </div>
  );
}
